import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { KRDS, tooltipStyles } from '~/components/charts/chart-colors'

export interface DonutDatum {
    name: string
    value: number
    color?: string
}

interface DonutChartProps {
    data: DonutDatum[]
    /** 가운데 큰 숫자(예: 총합) */
    centerValue?: string | number
    centerLabel?: string
    height?: number
}

// 분포용 도넛. KRDS 색·흰 카드 툴팁. 한국어 범례는 우측에 별도 렌더.
export function DonutChart({
    data,
    centerValue,
    centerLabel,
    height = 200,
}: DonutChartProps) {
    const total = data.reduce((s, d) => s + d.value, 0)

    return (
        <div className='flex items-center gap-5'>
            <div
                className='relative shrink-0'
                style={{ width: height, height }}>
                <ResponsiveContainer width='100%' height='100%'>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey='value'
                            nameKey='name'
                            innerRadius='62%'
                            outerRadius='100%'
                            paddingAngle={data.length > 1 ? 2 : 0}
                            stroke={KRDS.gray[0]}
                            strokeWidth={2}
                            isAnimationActive={false}>
                            {data.map((d, i) => (
                                <Cell key={i} fill={d.color ?? KRDS.gray[40]} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(v, n) => [`${v ?? 0}건`, String(n)]}
                            contentStyle={tooltipStyles.contentStyle}
                            itemStyle={tooltipStyles.itemStyle}
                            labelStyle={tooltipStyles.labelStyle}
                        />
                    </PieChart>
                </ResponsiveContainer>
                {centerValue !== undefined ? (
                    <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
                        <span className='text-foreground text-2xl font-bold'>
                            {centerValue}
                        </span>
                        {centerLabel ? (
                            <span className='krds-label mt-0.5'>
                                {centerLabel}
                            </span>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <ul className='flex-1 space-y-2'>
                {data.map((d) => {
                    const pct =
                        total > 0 ? Math.round((d.value / total) * 100) : 0
                    return (
                        <li
                            key={d.name}
                            className='flex items-center gap-2 text-sm'>
                            <span
                                className='inline-block size-2.5 shrink-0 rounded-full'
                                style={{ background: d.color ?? KRDS.gray[40] }}
                            />
                            <span className='text-foreground flex-1'>
                                {d.name}
                            </span>
                            <span className='text-foreground font-bold tabular-nums'>
                                {d.value}
                            </span>
                            <span className='text-muted-foreground w-10 text-right tabular-nums'>
                                {pct}%
                            </span>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
